import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Users, 
  Search,
  Loader2,
  UserPlus,
  Shield,
  User,
  Mail,
  X,
  MapPin,
  Building,
  Phone
} from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const COUNTRIES = [
  "China", "Japan", "South Korea", "Taiwan", "Hong Kong",
  "Singapore", "Malaysia", "Thailand", "Vietnam", "Indonesia", "Philippines",
  "India", "Bangladesh", "Pakistan", "Sri Lanka",
  "UAE", "Saudi Arabia", "Oman", "Qatar", "Bahrain", "Kuwait",
  "Germany", "Netherlands", "Belgium", "France", "United Kingdom", "Spain", "Italy", "Poland", "Greece", "Turkey",
  "United States", "Canada", "Mexico",
  "Brazil", "Argentina", "Chile", "Colombia", "Peru",
  "South Africa", "Egypt", "Morocco", "Nigeria", "Kenya",
  "Australia", "New Zealand"
].sort();

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    company: '',
    phone: '',
    country: '',
    role: 'user'
  });

  useEffect(function() {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      const response = await adminApi.getUsers();
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleRoleUpdate(userId, newRole) {
    try {
      await adminApi.updateUserRole(userId, { role: newRole });
      toast.success('User role updated');
      loadUsers();
    } catch (err) {
      toast.error('Failed to update role');
    }
  }

  function resetForm() {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      company: '',
      phone: '',
      country: '',
      role: 'user'
    });
  }

  async function handleCreateUser(e) {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.first_name || !formData.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      await api.post('/admin/users', formData);
      toast.success('User created successfully');
      setShowAddModal(false);
      resetForm();
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  }

  const filteredUsers = users.filter(function(user) {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  function getRoleBadgeColor(role) {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'agent': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }

  function getStatusBadgeColor(status) {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'pending_approval': return 'bg-amber-100 text-amber-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }

  function getRoleIcon(role) {
    switch (role) {
      case 'admin': return Shield;
      case 'agent': return Users;
      default: return User;
    }
  }

  const roleCounts = {
    all: users.length,
    user: users.filter(function(u) { return u.role === 'user'; }).length,
    agent: users.filter(function(u) { return u.role === 'agent'; }).length,
    admin: users.filter(function(u) { return u.role === 'admin'; }).length
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-[32px] sv-display text-ink">Users</h1>
          <p className="text-gray-600 mt-1">Manage user accounts and assign roles</p>
        </div>
        <Button 
          className="bg-ink hover:bg-ink-800 text-white rounded-full"
          onClick={function() { setShowAddModal(true); }}
        >
          <UserPlus className="mr-2 h-4 w-4" /> Add User
        </Button>
      </div>

      {/* Role Tabs */}
      <div className="flex flex-wrap gap-2">
        {['all', 'user', 'agent', 'admin'].map(function(role) {
          const isActive = roleFilter === role;
          const label = role === 'all' ? 'All Users' : role.charAt(0).toUpperCase() + role.slice(1) + 's';
          return (
            <button
              key={role}
              onClick={function() { setRoleFilter(role); }}
              className={"px-4 py-2 rounded-full text-sm font-medium transition-colors " + (
                isActive 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {label} ({roleCounts[role]})
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="bg-white rounded-sm shadow-card p-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search users by name or email..."
            className="pl-12 h-12 rounded-sm"
            value={searchTerm}
            onChange={function(e) { setSearchTerm(e.target.value); }}
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-sm shadow-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : filteredUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">User</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Email</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Country</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Role</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Joined</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(function(user) {
                  const RoleIcon = getRoleIcon(user.role);
                  return (
                    <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {user.first_name[0]}{user.last_name[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-ink">{user.first_name} {user.last_name}</p>
                            <p className="text-xs text-gray-500">{user.company || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{user.email}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <p className="text-sm text-gray-600">{user.country || '-'}</p>
                      </td>
                      <td className="py-4 px-6">
                        <Badge className={getStatusBadgeColor(user.status) + " rounded-full px-3 py-1 text-xs"}>
                          {(user.status || 'approved').replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="py-4 px-6">
                        <Badge className={getRoleBadgeColor(user.role) + " rounded-full px-3 py-1"}>
                          <RoleIcon className="h-3 w-3 mr-1" />
                          {user.role}
                        </Badge>
                      </td>
                      <td className="py-4 px-6">
                        <p className="text-sm text-gray-600">{formatDate(user.created_at)}</p>
                      </td>
                      <td className="py-4 px-6">
                        <Select 
                          value={user.role} 
                          onValueChange={function(value) { handleRoleUpdate(user.id, value); }}
                        >
                          <SelectTrigger className="w-[120px] rounded-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="agent">Agent</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-16">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No users found</p>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-medium tracking-[-0.01em] text-ink">Add new user</h2>
              <Button 
                variant="ghost" 
                size="sm" 
                className="rounded-full"
                onClick={function() { setShowAddModal(false); resetForm(); }}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-medium">First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={function(e) { setFormData({...formData, first_name: e.target.value}); }}
                    className="h-11 rounded-sm"
                    placeholder="John"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={function(e) { setFormData({...formData, last_name: e.target.value}); }}
                    className="h-11 rounded-sm"
                    placeholder="Smith"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={function(e) { setFormData({...formData, email: e.target.value}); }}
                  className="h-11 rounded-sm"
                  placeholder="user@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={function(e) { setFormData({...formData, password: e.target.value}); }}
                  className="h-11 rounded-sm"
                  placeholder="Minimum 6 characters"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-medium">Country</Label>
                  <Select value={formData.country} onValueChange={function(v) { setFormData({...formData, country: v}); }}>
                    <SelectTrigger className="h-11 rounded-sm">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(function(country) {
                        return <SelectItem key={country} value={country}>{country}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Role</Label>
                  <Select value={formData.role} onValueChange={function(v) { setFormData({...formData, role: v}); }}>
                    <SelectTrigger className="h-11 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="agent">Agent</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-medium">Company</Label>
                  <Input
                    value={formData.company}
                    onChange={function(e) { setFormData({...formData, company: e.target.value}); }}
                    className="h-11 rounded-sm"
                    placeholder="Company name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={function(e) { setFormData({...formData, phone: e.target.value}); }}
                    className="h-11 rounded-sm"
                    placeholder="+1 234 567"
                  />
                </div>
              </div>

              <div className="bg-blue-50 rounded-sm p-3 text-sm text-blue-800">
                <strong>Note:</strong> Users created by admin are automatically approved.
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button"
                  variant="outline" 
                  className="flex-1 h-12 rounded-full"
                  onClick={function() { setShowAddModal(false); resetForm(); }}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  className="flex-1 h-12 bg-ink hover:bg-ink-800 rounded-full"
                  disabled={creating}
                >
                  {creating ? (
                    <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Creating...</>
                  ) : (
                    'Create User'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
